import type React from "react"
import { useEffect, useMemo, useRef, useState } from "react"
import { Head, usePage } from "@inertiajs/react"
import { Clock, X } from "lucide-react"
import PublicLayout from "@/components/PublicLayout"
import { HeroBand, HeroFilm, type HeroPhoto } from "@/components/gisela/HeroBand"
import { BrandMotif } from "@/components/ui/brand-motif"
import GiselaPageTiles from "@/components/gisela/GiselaPageTiles"
import { TestimonialsBand } from "@/components/gisela/TestimonialsBand"
import { useMobilePager } from "@/components/MobilePager"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import { CategoryTag } from "@/components/ui/category-tag"
import { useLocalePath } from "@/lib/locale-path"
import { sectionHidden } from "@/lib/section-visibility"
import type { PageMeta, PageProps, PlacementPhoto, PublicTestimonial } from "@/types/inertia"

interface Article {
  id: number
  slug: string
  /** Already in the reader's language — a Spanish piece has a Spanish address. */
  path: string
  title: string
  excerpt: string | null
  category: string
  author: string
  read_time: number | null
  thumbnail: string | null
  /** Cover photo (card variant); falls back to the emoji/path thumbnail. */
  photo_url: string | null
  /** The condition or therapy page this piece supports — what the filter keys on. */
  topic_slug: string | null
  published_at: string | null
  /** Only ever true for a signed-in admin: a draft shown as a preview. */
  draft?: boolean
}

interface Topic {
  slug: string
  name: string
  /** The difficulty's own page, so a reader can go from the writing to the treatment. */
  path: string | null
  count: number
}

interface TopicGroup {
  key: string
  heading: string
  items: Topic[]
}

interface Props {
  articles: Article[]
  /** Photos for the closing tiles, from the same slot the home page uses. */
  tile_photos?: Record<string, PlacementPhoto | null>
  /** The published reviews, as on the home page. */
  testimonials?: PublicTestimonial[]
  /** The groups "What I can help with" uses, plus the OCD subtypes. */
  topics?: TopicGroup[]
  /** True when a signed-in admin is looking: drafts are listed, marked. */
  preview?: boolean
  content: {
    index_header: { title: string; subtitle: string; photo?: HeroPhoto | null; _hidden?: boolean }
    /** The closing contact band. Copy is fixed; only its background is editable. */
    cta?: { photo?: HeroPhoto | null; _hidden?: boolean }
  }
  meta?: PageMeta
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return ""
  return new Date(dateStr).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })
}

export default function BlogIndex({
  articles,
  topics = [],
  tile_photos = {},
  testimonials = [],
  content,
  meta,
  preview = false,
}: Props) {
  const { translations, current_brand, features } = usePage<PageProps>().props
  const t = translations.blog.index
  const tc = translations.common
  const th = translations.gisela_pages.hero
  const tn = translations.gisela_nav
  // Sanctuary can still serve this page before the split goes live, and the
  // closing band's button is one of Gisela's paths.
  const onGiselaHost = current_brand.key === "gisela"
  const lp = useLocalePath()

  // Which difficulty the reader is looking at, and what they typed. Both live
  // in the browser: every article is already in props, so filtering is instant
  // and a crawler still receives the whole list in the first render.
  const [topic, setTopic] = useState("")
  const [query, setQuery] = useState("")
  const flatTopics = useMemo(() => topics.flatMap((group) => group.items), [topics])
  const selected = flatTopics.find((item) => item.slug === topic) ?? null

  // A ?topic= link — from an article page, or one someone shared — lands
  // filtered. Read after mount rather than during render so the server's
  // markup and the hydrating client's agree; an unknown slug is ignored
  // rather than filtering the page down to nothing.
  useEffect(() => {
    const wanted = new URLSearchParams(window.location.search).get("topic")
    if (wanted && flatTopics.some((item) => item.slug === wanted)) setTopic(wanted)
  }, [flatTopics])

  // Picking a difficulty from the top of a long index would otherwise leave
  // the reader looking at the index while the answer sits below the fold, so
  // the results come to them. Not on first render, and not while they type:
  // the search box is already beside what it filters.
  const resultsRef = useRef<HTMLDivElement>(null)
  const landed = useRef(false)
  useEffect(() => {
    if (!landed.current) {
      landed.current = true
      return
    }
    resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
  }, [topic])

  // Keep the address bar in step, so a filtered view can be shared or kept.
  useEffect(() => {
    const url = new URL(window.location.href)
    if (topic) url.searchParams.set("topic", topic)
    else url.searchParams.delete("topic")
    window.history.replaceState(window.history.state, "", url)
  }, [topic])

  const filtered = useMemo(() => {
    const words = query.trim().toLowerCase()
    return articles.filter((article) => {
      if (topic && article.topic_slug !== topic) return false
      if (!words) return true
      return `${article.title} ${article.excerpt ?? ""} ${article.category ?? ""}`.toLowerCase().includes(words)
    })
  }, [articles, topic, query])

  // Six to a page on a laptop — two rows of three, so the library never opens
  // as a hundred-card scroll — and one at a time on a phone, where a card is
  // most of the screen. The count rides the results heading either way; the
  // arrows sit beside it on a laptop and on the card's own edges on a phone,
  // where the cards are swiped as well.
  const pager = useMobilePager(filtered.length, {
    pageSize: 6,
    phonePageSize: 1,
    variant: "compact",
    resetKey: `${topic}|${query}`,
    controlsClassName: "flex items-center gap-2",
    edgeArrows: true,
  })

  // Follows the host, like Show.tsx — each brand's blog names its own site.
  const brandName = current_brand.name
  const title = meta?.title ?? `Blog — ${brandName}`
  const description =
    meta?.description ?? `Articles, guides and resources from ${brandName}.`
  const headerShown = !sectionHidden(content.index_header)
  // Searching and browsing are different jobs. While a search is running the
  // topic index stands aside, so the matches sit directly under the box the
  // reader is typing into — with it in the way, a search changed only things
  // a thousand pixels below the fold and looked broken.
  const searching = query.trim().length > 0
  const clearFilters = () => {
    setTopic("")
    setQuery("")
  }
  const countLabel = (filtered.length === 1 ? t.count_one : t.count_other).replace(
    "%{count}",
    String(filtered.length),
  )

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <PublicLayout>
        {/* The hero every page on her site opens with: the admin's chosen
            photo under the blue film, or the plain blue band. Widened to the
            page's own container so the heading lines up with the search box
            and the article grid below it. */}
        {headerShown && (
          <HeroBand photo={content.index_header.photo} className="max-w-7xl">
            <h1 className="text-white">{content.index_header.title}</h1>
            {content.index_header.subtitle && (
              <p className="mt-4 max-w-2xl text-lg text-white/80">{content.index_header.subtitle}</p>
            )}
          </HeroBand>
        )}

        {/* Search, then the topic index. The groups and their order are the
            ones "What I can help with" uses, so the blog reads as the same
            library of difficulties rather than a second, unrelated taxonomy. */}
        <section className={`bg-stone pb-12 ${headerShown ? "pt-12" : "pt-32"}`}>
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="max-w-md">
              <label htmlFor="blog-search">{t.search_label}</label>
              <Input
                id="blog-search"
                type="search"
                value={query}
                placeholder={t.search_placeholder}
                onChange={(event) => setQuery(event.target.value)}
                className="mt-1.5"
              />
              {searching && (
                <div className="mt-2 flex flex-wrap items-center gap-x-3">
                  <p className="text-sm text-ink-muted" aria-live="polite">{countLabel}</p>
                  <Button type="button" variant="link" size="sm" onClick={clearFilters}>
                    {t.clear}
                  </Button>
                </div>
              )}
            </div>

            {flatTopics.length > 0 && !searching && (
              <>
                {/* Phones: one grouped menu. A chip row of thirty topics would
                    fill the screen before a single article did. */}
                <div className="mt-6 max-w-md sm:hidden">
                  <label htmlFor="blog-topic">{t.browse_label}</label>
                  <Select
                    id="blog-topic"
                    value={topic}
                    onChange={(event) => setTopic(event.target.value)}
                    className="mt-1.5"
                  >
                    <option value="">{t.all_topics}</option>
                    {topics.map((group) => (
                      <optgroup key={group.key} label={group.heading}>
                        {group.items.map((item) => (
                          <option key={item.slug} value={item.slug}>
                            {item.name} ({item.count})
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </Select>
                </div>

                {/* Everything wider: the whole taxonomy on show. The groups
                    flow down columns rather than across the page, because a
                    name read down a column is found faster than one hunted
                    along a wrapped row — and these names are long. Each group
                    stays whole in its column. */}
                <div className="mt-8 hidden sm:block">
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                    <p className="text-sm font-medium text-ink-display">{t.browse_prompt}</p>
                    {/* Offered only when there is something to clear. Clicking
                        the chosen topic again does the same job; this is the
                        second, more findable way out, for a reader who has
                        scrolled past the one row carrying the dot. There is no
                        "every topic" button any more — nothing selected already
                        means every topic, and a button for the state you are
                        already in is one more thing to read. */}
                    {selected && (
                      <Button
                        type="button"
                        size="sm"
                        variant="link"
                        onClick={() => setTopic("")}
                        /* The same amber the chosen topic is filled in, so the
                           mark and the way to remove it are plainly one thing.
                           The display shade rather than the fill's own: #fcd34d
                           is a background colour, and too pale to read as text
                           on cream. */
                        className="gap-1 px-1 text-signal-display"
                      >
                        <X className="size-4" aria-hidden="true" />
                        {t.clear_topic}
                      </Button>
                    )}
                  </div>
                  {/* Columns by width, not by breakpoint: given a 391px
                      minimum the browser fits three across a wide page, two on
                      a laptop, one on a small tablet, and never a column too
                      narrow to hold the longest name (386px) on one line. The
                      figure is deliberately just above that name: any wider and
                      a 1292px page takes only two columns. A pair of
                      `columns-N` utilities could not do this — Tailwind emits
                      the wider breakpoint's rule first, so the narrower one
                      wins the cascade at every size. */}
                  <div className="mt-6 columns-[391px] gap-x-12">
                    {topics.map((group) => (
                      <fieldset key={group.key} className="mb-6 break-inside-avoid">
                        <legend>{group.heading}</legend>
                        {/* items-start, not items-stretch: the chosen row is filled in, and
                            a full-column-width amber bar dominates the whole
                            index. Sized to its own name it reads as a mark on
                            that topic. */}
                        <div className="mt-1 flex flex-col items-start gap-0.5">
                          {group.items.map((item) => (
                            <Button
                              key={item.slug}
                              type="button"
                              size="sm"
                              variant="link"
                              aria-pressed={topic === item.slug}
                              /* Picking the chosen topic again clears it, so
                                 the row that switched the filter on is also
                                 the one that switches it off. */
                              onClick={() => setTopic(topic === item.slug ? "" : item.slug)}
                              /* Written as the index of links it is: accent
                                 text so it reads as something to click, and
                                 left-aligned, because a taxonomy name is
                                 longer than a button label. */
                              /* Chosen: the amber rectangle the draft-preview
                                 notice on this page already uses, so "this one
                                 is on" looks the same wherever the blog says
                                 it. The border is on every row, transparent
                                 until chosen, so filling one in does not nudge
                                 the column. */
                              className={`h-auto justify-start whitespace-normal rounded-lg border px-2 py-1.5 text-left ${
                                topic === item.slug
                                  ? "border-signal bg-signal-faded text-ink-body"
                                  : "border-transparent"
                              }`}
                            >
                              {item.name} ({item.count})
                            </Button>
                          ))}
                        </div>
                      </fieldset>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </section>

        {/* Article grid */}
        <section className="bg-cream py-14">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            {preview && articles.some((a) => a.draft) && (
              <p role="status" className="mb-8 rounded-lg border border-signal bg-signal-faded px-4 py-3 text-sm text-ink-body">
                <strong>Draft preview.</strong> Articles marked as drafts are shown to you because you are signed in
                as an admin; visitors will not see them until they are set to Published.
              </p>
            )}
            {articles.length > 0 ? (
              <>
              <div
                ref={resultsRef}
                className="flex scroll-mt-[calc(var(--admin-bar-h)+5rem)] flex-wrap items-center justify-between gap-x-6 gap-y-3"
              >
                <h2>
                  {searching
                    ? t.results_for.replace("%{query}", query.trim())
                    : selected
                      ? selected.name
                      : t.all_articles}
                </h2>
                {/* How many there are, and the way through them, on the
                    heading's own line. */}
                <div className="flex items-center gap-4">
                  <p className="text-sm text-ink-muted" aria-live={searching ? "off" : "polite"}>{countLabel}</p>
                  {pager.controls}
                </div>
              </div>
              {/* From the writing to the treatment: the difficulty's own page. */}
              {selected?.path && (
                <p className="mt-2">
                  <a
                    href={selected.path}
                    className="font-medium text-teal-mid no-underline hover:text-teal hover:underline"
                  >
                    {t.read_page.replace("%{name}", selected.name)}
                  </a>
                </p>
              )}
              {filtered.length > 0 ? (
                <div
                  ref={pager.listRef as React.RefObject<HTMLDivElement>}
                  className="relative mt-6 grid scroll-mt-24 grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
                >
                  {/* Absolutely positioned, so the arrows ride the grid's own
                      edges without taking a cell of it. */}
                  {pager.edgeControls}
                  {filtered.map((article, index) => (
                    <a
                      key={article.id}
                      href={article.path}
                      className={`group relative flex flex-col overflow-hidden rounded-xl bg-white ring-1 ring-hairline transition-shadow hover:shadow-md ${pager.itemClass(index)}`}
                    >
                      {article.draft && (
                        <span className="absolute top-3 left-3 z-10 inline-flex items-center rounded-full border border-signal bg-signal-faded px-2 py-0.5 text-xs font-medium text-ink-body">
                          Draft preview
                        </span>
                      )}
                      {/* A card with no cover photo gets a shallow emoji strip
                          rather than a picture-shaped empty box. */}
                      <div
                        className={`flex items-center justify-center bg-stone text-4xl ${
                          article.photo_url || article.thumbnail?.startsWith("/") ? "h-40" : "h-24 sm:h-28"
                        }`}
                      >
                        {article.photo_url ? (
                          <img src={article.photo_url} alt={article.title} className="h-full w-full object-cover" />
                        ) : article.thumbnail && !article.thumbnail.startsWith("/") ? (
                          <span>{article.thumbnail}</span>
                        ) : article.thumbnail ? (
                          <img src={article.thumbnail} alt={article.title} className="h-full w-full object-cover" />
                        ) : (
                          <span>📄</span>
                        )}
                      </div>
                      <div className="flex flex-1 flex-col p-5">
                        <CategoryTag label={article.category} />
                        <h3 className="mt-3 text-navy-deep group-hover:text-teal-mid">
                          {article.title}
                        </h3>
                        {article.excerpt && (
                          <p className="mt-2 line-clamp-3 text-sm text-ink-muted">{article.excerpt}</p>
                        )}
                        <div className="mt-auto flex items-center gap-3 pt-4 text-xs text-ink-muted">
                          {article.read_time && (
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {tc.min_read.replace("%{count}", String(article.read_time))}
                            </span>
                          )}
                          {article.published_at && <span>{formatDate(article.published_at)}</span>}
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              ) : (
                <p className="py-16 text-center text-ink-muted">{t.no_results}</p>
              )}
              </>
            ) : (
              <p className="py-20 text-center text-ink-muted">{t.coming_soon}</p>
            )}
          </div>
        </section>

        {/* The way on. A reader who has just finished an article is closer to
            picking up the phone than at any other point on the site, so the
            blog ends on the same invitation the home page closes with. */}
        {onGiselaHost && !sectionHidden(content.cta) && (
          <section className="relative overflow-hidden bg-teal py-16 text-white">
            <HeroFilm photo={content.cta?.photo} />
            <BrandMotif variant="rings" tone="white" size={720} opacity={0.35} className="absolute -top-80 -left-64" />
            <div className="relative mx-auto max-w-3xl px-6 text-center lg:px-8">
              <h2 className="text-white">{th.cta_heading}</h2>
              <p className="mt-3 text-white/75">{th.cta_body}</p>
              <div className="mt-7">
                <a
                  href={lp("/book")}
                  className="rounded-full bg-white px-6 py-2.5 text-sm font-medium text-teal no-underline shadow transition-opacity hover:opacity-90"
                >
                  {tn.book_call}
                </a>
              </div>
            </div>
          </section>
        )}

        {/* Somewhere to go next, and a light band between the contact band and
            the footer — both are blue, and butted together they read as one
            long blue block with the invitation lost in it. The blog's own tile
            is left out — it is the page the reader is on — and Therapies I
            Offer takes its place: someone who has just read a piece about a
            difficulty is being shown the work that treats it. */}
        <GiselaPageTiles photos={tile_photos} exclude={["/blog"]} />

        {/* What other people found, last before the footer. The tiles above
            are stone, so this one keeps the component's cream. */}
        {features.testimonials_display && <TestimonialsBand testimonials={testimonials} />}
      </PublicLayout>
    </>
  )
}
